/**
 * Token Refresh Handler
 * Handles 401 Unauthorized errors by refreshing tokens
 */

import type { AxiosInstance, AxiosError } from "axios";
import {
  HttpStatusCode,
  type ExtendedAxiosRequestConfig,
  type InterceptorConfig,
} from "../../types";
import {
  getCrossTabSync,
  CrossTabMessageType,
  type TokenRefreshSuccessPayload,
} from "../../cross-tab";
import { shouldLog } from "../../utils/logging";
import { getTokenRefreshManager } from "../../utils/token-refresh-manager";

/**
 * Get client ID from request config
 * Uses baseURL as client identifier to support per-client refresh state
 */
function getClientId(config: ExtendedAxiosRequestConfig): string {
  return config.baseURL || "default";
}

function setRetriedRequestAuthHeaders(
  headers: ExtendedAxiosRequestConfig["headers"],
  accessToken: string,
): void {
  if (!headers) return;

  const bearerToken = `Bearer ${accessToken}`;
  const maybeAxiosHeaders = headers as {
    set?: (name: string, value: string) => void;
  };

  if (typeof maybeAxiosHeaders.set === "function") {
    maybeAxiosHeaders.set("Authorization", bearerToken);
    maybeAxiosHeaders.set("accesstoken", accessToken);
    return;
  }

  const mutableHeaders = headers as Record<string, string>;
  mutableHeaders.Authorization = bearerToken;
  mutableHeaders.accesstoken = accessToken;
}

/**
 * Handles token refresh for 401 Unauthorized errors
 */
export async function handleTokenRefresh(
  error: AxiosError,
  originalRequest: ExtendedAxiosRequestConfig,
  axiosInstance: AxiosInstance,
  config: InterceptorConfig,
): Promise<unknown> {
  const { tokenStorage, refreshTokenFn, onAuthFailure, metrics } = config;

  if (!refreshTokenFn || !tokenStorage) {
    if (onAuthFailure) {
      onAuthFailure();
    }
    throw error;
  }

  if (
    error.response?.status !== HttpStatusCode.UNAUTHORIZED ||
    originalRequest._retry
  ) {
    throw error;
  }

  originalRequest._retry = true;
  const clientId = getClientId(originalRequest);
  const crossTabSync = getCrossTabSync();
  const tokenRefreshManager = getTokenRefreshManager();

  // Check if refresh is in progress for this client or globally
  if (
    tokenRefreshManager.isRefreshing(clientId) ||
    crossTabSync.isRefreshingInOtherTab()
  ) {
    if (shouldLog(config)) {
      console.log(
        `[API] Token refresh in progress for client "${clientId}", queueing request...`,
      );
    }

    const subscriberCount = tokenRefreshManager.getSubscriberCount(clientId);
    if (metrics?.onRequestQueued) {
      metrics.onRequestQueued(subscriberCount + 1);
    }

    return new Promise((resolve, reject) => {
      tokenRefreshManager.subscribe(clientId, {
        onSuccess: async (newToken: string) => {
          try {
            setRetriedRequestAuthHeaders(originalRequest.headers, newToken);
            resolve(axiosInstance.request(originalRequest));
          } catch (err) {
            const apiErr =
              err instanceof Error ? err : new Error("Request retry failed");
            reject(apiErr);
          }
        },
        onFailure: (refreshError: Error) => {
          reject(refreshError);
        },
      });
    });
  }

  // Start refresh for this client
  tokenRefreshManager.setRefreshing(clientId, true);
  crossTabSync.broadcast({ type: CrossTabMessageType.TOKEN_REFRESH_START });

  if (metrics?.onTokenRefreshStart) {
    metrics.onTokenRefreshStart();
  }

  const refreshStartTime = Date.now();

  try {
    const tokens = await crossTabSync.withLock(async () => {
      if (shouldLog(config)) {
        console.log(
          `[API] Attempting token refresh for client "${clientId}"...`,
        );
      }

      return refreshTokenFn();
    });

    try {
      await Promise.resolve(
        tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken),
      );
    } catch (storageError) {
      console.error(
        "[API] Failed to store tokens after refresh:",
        storageError,
      );
      // Continue even if storage fails - token is still valid for this request
    }

    const refreshDuration = Date.now() - refreshStartTime;

    if (shouldLog(config)) {
      console.log(
        `[API] Token refresh successful for client "${clientId}" (${refreshDuration}ms)`,
      );
    }

    crossTabSync.notifyTokenUpdate({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    });

    setRetriedRequestAuthHeaders(originalRequest.headers, tokens.accessToken);

    // Notify subscribers for this client
    tokenRefreshManager.notifySuccess(clientId, tokens.accessToken);

    if (metrics?.onTokenRefreshSuccess) {
      metrics.onTokenRefreshSuccess(refreshDuration);
    }

    return axiosInstance.request(originalRequest);
  } catch (refreshError) {
    if (shouldLog(config)) {
      console.error(
        `[API] Token refresh failed for client "${clientId}":`,
        refreshError,
      );
    }

    crossTabSync.broadcast({ type: CrossTabMessageType.TOKEN_REFRESH_FAILURE });

    try {
      await Promise.resolve(tokenStorage.clearTokens());
    } catch (clearError) {
      console.error(
        "[API] Failed to clear tokens after refresh failure:",
        clearError,
      );
    }

    const error =
      refreshError instanceof Error
        ? refreshError
        : new Error("Token refresh failed");

    // Notify subscribers for this client
    tokenRefreshManager.notifyFailure(clientId, error);

    if (metrics?.onTokenRefreshFailure) {
      metrics.onTokenRefreshFailure(error);
    }

    if (onAuthFailure) {
      onAuthFailure();
    }

    throw refreshError;
  }
}

/**
 * Handles cross-tab token refresh messages
 */
export function setupCrossTabTokenRefresh(
  config: InterceptorConfig,
): () => void {
  const { tokenStorage, onAuthFailure, metrics } = config;
  const crossTabSync = getCrossTabSync();
  const tokenRefreshManager = getTokenRefreshManager();

  return crossTabSync.onMessage(async (message) => {
    try {
      switch (message.type) {
        case CrossTabMessageType.TOKEN_REFRESH_SUCCESS: {
          const payload = message.payload as TokenRefreshSuccessPayload;
          if (tokenStorage && payload) {
            if (shouldLog(config)) {
              console.log("[API] Token updated from another tab");
            }
            try {
              await Promise.resolve(
                tokenStorage.setTokens(
                  payload.accessToken,
                  payload.refreshToken,
                ),
              );

              // Notify all clients (cross-tab refresh affects all clients)
              // We use a special 'all' client ID for cross-tab updates
              // Also notify 'default' client for backward compatibility
              tokenRefreshManager.notifySuccess("all", payload.accessToken);
              tokenRefreshManager.notifySuccess("default", payload.accessToken);

              if (metrics?.onTokenRefreshSuccess) {
                metrics.onTokenRefreshSuccess(0);
              }
            } catch (error) {
              console.error(
                "[API] Failed to store tokens from cross-tab update:",
                error,
              );
            }
          }
          break;
        }
        case CrossTabMessageType.TOKEN_REFRESH_FAILURE: {
          if (shouldLog(config)) {
            console.log("[API] Token refresh failed in another tab");
          }
          const error = new Error("Token refresh failed in another tab");
          // Notify all clients
          tokenRefreshManager.notifyFailure("all", error);

          if (metrics?.onTokenRefreshFailure) {
            metrics.onTokenRefreshFailure(error);
          }
          if (onAuthFailure) {
            onAuthFailure();
          }
          break;
        }
        case CrossTabMessageType.LOGOUT: {
          if (shouldLog(config)) {
            console.log("[API] Logout from another tab");
          }
          if (tokenStorage) {
            try {
              await Promise.resolve(tokenStorage.clearTokens());
            } catch (error) {
              console.error(
                "[API] Failed to clear tokens on cross-tab logout:",
                error,
              );
            }
          }
          if (onAuthFailure) {
            onAuthFailure();
          }
          break;
        }
      }
    } catch (error) {
      console.error("[API] Error handling cross-tab message:", error);
    }
  });
}
